import React from "react";
import { DateTime } from "luxon";
import classes from "./BookingForm.module.scss";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { selectGoogleClickId, selectUnitData } from "../../store/bookingSlice";
import { useLoaderData, useNavigate } from "react-router-dom";
import Location from "../../features/rapidstor/entities/Location";
import { determineFloor, UnitGroup } from "../../features/rapidstor/entities/UnitType";
import { ReactComponent as IconUnitGroup } from "../../assets/icon-unit-group.svg";
import { useImmer } from "use-immer";
import {
  CorpCode,
  usePostReservationMutation,
  usePatchReservationMutation,
} from "../../features/rapidstor/rapidstorApiSlice";
import UnitPrice from "../UnitListing/UnitPrice";
import {
  reservationEvent,
  reservationEventPayload,
  reservationViaMoveInEvent,
} from "../../features/rapidstor/PostMessageEvents";
import { setCookie, getCookie } from "../../utils/cookies";
import { UnitData } from "../../features/rapidstor/entities/UnitType";

function BookingForm() {
  const unitData = useSelector(selectUnitData);
  const navigate = useNavigate();
  const location = useLoaderData() as Location;
  const [makeReservation, { isLoading: reservationLoading }] =
    usePostReservationMutation();
  const GCLID = useSelector(selectGoogleClickId);

  // Only use reservationToken if it matches the current location
  const reservationTokenRaw = decodeURIComponent(
    getCookie("reservationToken") || getCookie("quoteToken") || ""
  );
  const reservationToken: string | null =
    reservationTokenRaw && reservationTokenRaw.split(":")[0] === location.locationCode
      ? reservationTokenRaw.split(":")[1] || null
      : null;
  const [patchReservation] = usePatchReservationMutation();

  React.useEffect(() => {
    if (!unitData) {
      navigate(`/locations/${location.slug}`);
    }
  }, [location.slug, navigate, unitData]);

  function getReservationPayload(
    form: Record<string, string>,
    unitData: UnitData,
    location: Location,
    neededDate: string,
    GCLID: string | null
  ) {
    return {
      customListing: true,
      deal: unitData.unitType.deal?._id,
      neededDate,
      timezoneOffset: location.timezoneOffset,
      sourceUrl: window.location.href,
      tenant: {
        tenantId: null,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
      },
      unitType: unitData.unitType._id,
      unitToUse: unitData.unitType.units[0]._id,
      googleClickId: GCLID ? GCLID : undefined,
    };
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const neededDate = DateTime.fromFormat(form.moveInDate, "yyyy-MM-dd").toISO();
    if (!neededDate) {
      console.error("Invalid move in date", form.moveInDate);
      return;
    }
    if (!unitData) {
      console.error("No unit data");
      return;
    }
    const payload = getReservationPayload(form, unitData, location, neededDate, GCLID);

    if (reservationToken) {
      // PATCH logic
      try {
        await patchReservation({
          reservationId: reservationToken,
          locationCode: location.locationCode,
          payload,
        });
        navigate(
          `/locations/${location.slug}/booking#/wizard/movein/single-page/complete-move-in/${reservationToken}`
        );
      } catch (err) {
        console.error(err);
      }
      return;
    }

    try {
      const newReservation = await makeReservation({
        corpCode: CorpCode,
        locationCode: location.locationCode,
        payload,
      }).unwrap();
      const { token } = newReservation;

      const eventPayload = reservationEventPayload(
        newReservation,
        location,
        unitData.unitType.deal?.title || null
      );
      // The embedded wizard won't fire this itself, as we hand it an existing reservation.
      if (unitData.reservation) {
        reservationEvent(eventPayload);
      } else {
        reservationViaMoveInEvent(eventPayload);
      }

      setCookie("reservationToken", `${location.locationCode}:${token}`);
      console.log("Reservation token:", token);
      navigate(
        `/locations/${location.slug}/booking#/wizard/movein/single-page/complete-move-in/${token}`
      );
    } catch (err) {
      console.error(err);
    }
  }

  const [form, updateForm] = useImmer<Record<string, string>>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    moveInDate: "",
  });

  if (!unitData) {
    return null;
  }

  const { reservation, unitType } = unitData;

  const groupLabels: Record<UnitGroup, string> = {
    upper: "Upper Level",
    ground: "Ground Floor",
    driveup: "Drive Up",
  };

  const formFilled =
    form.firstName && form.lastName && form.email && form.phone && form.moveInDate;

  function update(key: string, event: React.BaseSyntheticEvent) {
    updateForm((draft) => {
      draft[key] = String(event.target.value);
      return draft;
    });
  }

  return (
    <div className={"d-flex flex-column row-gap-2"}>
      <div className={`${classes.header} fw-semibold pt-3`}>
        {reservation ? "Reserve Unit" : "Book Unit"}
      </div>
      <div className={"d-flex justify-content-between align-items-end"}>
        <div>
          <div className={"fw-semibold text-primary mb-1"}>
            {unitType.width}m x {unitType.length}m {unitType.typeName}
          </div>
          <div className={classes.basicDetails}>
            <div>
              <IconUnitGroup className={"text-primary me-2"} style={{ width: "12px" }} />
              <span>
                {groupLabels[determineFloor(unitType, location.rapidstor.groundFloor)]}
              </span>
            </div>
          </div>
        </div>
        <UnitPrice unitType={unitType} />
      </div>

      <Form className={`${classes.form} d-flex flex-column row-gap-1`} onSubmit={submit}>
        <Form.Control
          type={"text"}
          placeholder={"First Name*"}
          required
          onChange={(e) => update("firstName", e)}
        />
        <Form.Control
          type={"text"}
          placeholder={"Last Name*"}
          required
          onChange={(e) => update("lastName", e)}
        />
        <Form.Control
          type={"email"}
          placeholder={"Email*"}
          required
          onChange={(e) => update("email", e)}
        />
        <Form.Control
          type={"tel"}
          placeholder={"Phone*"}
          required
          onChange={(e) => update("phone", e)}
        />
        <Form.FloatingLabel label={"Move-In Date*"} className={"mb-3"}>
          <Form.Control
            type={"date"}
            required
            onChange={(e) => update("moveInDate", e)}
            min={new Date().toISOString().split("T")[0]}
            max={
              DateTime.now()
                .plus({ days: location.rapidstor.reservationNumberOfDaysInAdvanceLimit })
                .toISODate() || undefined
            }
          />
        </Form.FloatingLabel>

        {/*
        <SmallDetail bold>Our team will contact you to confirm:</SmallDetail>
        */}
        <div className={classes.dotPoints}>
          {/*
          <ul className={`${classes.dotPoints} lorem-ipsum`}>
            <li>Free move-in truck and trailer</li>
            <li>Boxes & padding supplies</li>
            <li>Insurance</li>
          </ul>
          */}
          {reservation && (
            <div>
              By clicking confirm you will be sent an email with payment instructions and
              further booking requirements.
            </div>
          )}
        </div>

        <Button
          className={"btn-md mt-2"}
          type={"submit"}
          disabled={!formFilled || reservationLoading}
        >
          {reservation ? "Confirm & Proceed to Payment" : "Confirm Booking"}
        </Button>
      </Form>

      <div className={`${classes.disclaimer} text-center opacity-50`}>
        Enter your details above, and we will save your information to proceed with your
        unit booking.
      </div>
    </div>
  );
}
export default BookingForm;
